import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { FilterCombobox } from '../admin-ui';
import { useAdminVoiceProviders } from '../voice-admin-api';
import { useUsageBreakdown, useUsageMeta } from './ai-usage-api';
import DateRangeFilter, { ALL_TIME, describeRange } from './date-range';
import { featureGroups, labelFor, providerGroups } from './filter-groups';
import InterviewCostDrawer from './interview-cost-drawer';
import { modeName } from './labels';
import UsageBreakdownView from './usage-breakdown-view';
import UsageCallsView from './usage-calls-view';
import UsageOrganizationsView from './usage-organizations-view';
import UsageRecordDrawer from './usage-record-drawer';
const MODES = [
    { value: '', label: 'All modes' },
    { value: 'cost_optimized', label: 'Cost-optimized' },
    { value: 'realtime', label: 'Realtime' },
];
const EXTRA_NAMES = {
    model: 'Model',
    recruiter_id: 'Recruiter',
    phase: 'Stage',
};
const Chip = ({ name, value, onRemove, }) => (_jsxs("span", { className: 'inline-flex items-center gap-1 rounded-full border border-dash-border bg-dash-surface py-0.5 pl-2.5 pr-1 text-[11.5px]', children: [_jsxs("span", { className: 'text-dash-muted', children: [name, ":"] }), _jsx("span", { className: 'max-w-[180px] truncate font-medium text-dash-text', children: value }), _jsx("button", { type: 'button', onClick: onRemove, "aria-label": `Remove ${name} filter`, className: 'flex h-5 w-5 items-center justify-center rounded-full text-dash-muted hover:bg-dash-hover hover:text-dash-text cursor-pointer', children: _jsx(X, { className: 'h-3 w-3' }) })] }));
const Field = ({ label, children, className = '', }) => (_jsxs("div", { className: `flex min-w-0 flex-col gap-1 ${className}`, children: [_jsx("span", { className: 'text-[11px] font-medium uppercase tracking-wide text-dash-muted', children: label }), children] }));
export default function UsagePanel({ view = 'breakdown', onViewChange, onOpenInterview, organizationId, onOrganizationChange, } = {}) {
    var _a, _b, _c, _d;
    const [range, setRange] = useState(ALL_TIME);
    const [ownOrg, setOwnOrg] = useState('');
    const org = organizationId !== null && organizationId !== void 0 ? organizationId : ownOrg;
    const setOrg = onOrganizationChange !== null && onOrganizationChange !== void 0 ? onOrganizationChange : setOwnOrg;
    const [mode, setMode] = useState('');
    const [provider, setProvider] = useState('');
    const [feature, setFeature] = useState('');
    const [extras, setExtras] = useState([]);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [selectedInterview, setSelectedInterview] = useState(null);
    const { data: meta } = useUsageMeta();
    const { data: voiceProviders } = useAdminVoiceProviders();
    const voiceIds = useMemo(() => (voiceProviders !== null && voiceProviders !== void 0 ? voiceProviders : []).map((p) => p.provider_id), [voiceProviders]);
    const providerOpts = useMemo(() => providerGroups(meta === null || meta === void 0 ? void 0 : meta.providers, voiceIds), [meta, voiceIds]);
    const featureOpts = useMemo(() => featureGroups(meta), [meta]);
    // Unfiltered on purpose: picking an organization must not empty its own list.
    const orgList = useUsageBreakdown({ group_by: 'organization' });
    const orgOptions = ((_b = (_a = orgList.data) === null || _a === void 0 ? void 0 : _a.rows) !== null && _b !== void 0 ? _b : []).filter((r) => r.key !== '__unattributed__');
    const params = useMemo(() => {
        const p = {
            date_from: range.from || undefined,
            date_to: range.to || undefined,
            organization_id: org || undefined,
            mode: mode || undefined,
            provider: provider || undefined,
            feature: feature || undefined,
        };
        extras.forEach((e) => (p[e.param] = e.value));
        return p;
    }, [range, org, mode, provider, feature, extras]);
    /** Apply a breakdown row as a filter: its own control if it has one, else a chip. */
    const narrow = (param, value, label) => {
        if (param === 'organization_id')
            setOrg(value);
        else if (param === 'mode')
            setMode(value);
        else if (param === 'provider')
            setProvider(value);
        else if (param === 'feature')
            setFeature(value);
        else
            setExtras((prev) => [
                ...prev.filter((e) => e.param !== param),
                { param: param, value, label },
            ]);
    };
    const orgName = (_d = (_c = orgOptions.find((o) => o.key === org)) === null || _c === void 0 ? void 0 : _c.label) !== null && _d !== void 0 ? _d : org;
    const chips = [
        range.preset !== 'all' && {
            name: 'Period',
            value: describeRange(range),
            clear: () => setRange(ALL_TIME),
        },
        org && { name: 'Organization', value: orgName, clear: () => setOrg('') },
        mode && { name: 'Mode', value: modeName(mode), clear: () => setMode('') },
        provider && {
            name: 'Provider',
            value: labelFor(providerOpts, provider),
            clear: () => setProvider(''),
        },
        feature && {
            name: 'Feature',
            value: labelFor(featureOpts, feature),
            clear: () => setFeature(''),
        },
        ...extras.map((e) => ({
            name: EXTRA_NAMES[e.param],
            value: e.label,
            clear: () => setExtras((prev) => prev.filter((x) => x.param !== e.param)),
        })),
    ].filter(Boolean);
    const clearAll = () => {
        setRange(ALL_TIME);
        setOrg('');
        setMode('');
        setProvider('');
        setFeature('');
        setExtras([]);
    };
    const openInterview = (slug) => onOpenInterview ? onOpenInterview(slug) : setSelectedInterview(slug);
    return (_jsxs("div", { className: 'space-y-4', children: [_jsxs("div", { className: 'rounded-xl border border-dash-border bg-dash-card p-3', children: [_jsxs("div", { className: 'grid grid-cols-1 gap-3 aiSm:grid-cols-2 aiLg:grid-cols-5', children: [_jsx(Field, { label: 'Period', children: _jsx(DateRangeFilter, { compact: true, value: range, onChange: setRange }) }), _jsx(Field, { label: 'Organization', children: _jsxs("select", { value: org, onChange: (e) => setOrg(e.target.value), className: 'h-9 w-full rounded-lg border border-dash-border bg-dash-surface px-2.5 text-[12.5px] text-dash-text outline-none hover:border-dash-borderStrong focus:border-dash-accent cursor-pointer max-aiMd:h-11', children: [_jsx("option", { value: '', children: "All organizations" }), orgOptions.map((o) => (_jsx("option", { value: o.key, children: o.label }, o.key)))] }) }), _jsx(Field, { label: 'Interview mode', children: _jsx("select", { value: mode, onChange: (e) => setMode(e.target.value), className: 'h-9 w-full rounded-lg border border-dash-border bg-dash-surface px-2.5 text-[12.5px] text-dash-text outline-none hover:border-dash-borderStrong focus:border-dash-accent cursor-pointer max-aiMd:h-11', children: MODES.map((m) => (_jsx("option", { value: m.value, children: m.label }, m.value))) }) }), _jsx(FilterCombobox, { label: 'Provider', value: provider, onChange: setProvider, groups: providerOpts }), _jsx(FilterCombobox, { label: 'Feature', value: feature, onChange: setFeature, groups: featureOpts })] }), chips.length > 0 && (_jsxs("div", { className: 'mt-3 flex flex-wrap items-center gap-1.5 border-t border-dash-border pt-3', children: [chips.map((c) => (_jsx(Chip, { name: c.name, value: c.value, onRemove: c.clear }, c.name))), _jsx("button", { type: 'button', onClick: clearAll, className: 'ml-1 text-[11.5px] font-medium text-dash-accentInk hover:underline cursor-pointer', children: "Clear all" })] }))] }), view === 'breakdown' && (_jsx(UsageBreakdownView, { params: params, meta: meta, onNarrow: narrow, onViewCalls: (param, value, label) => {
                    narrow(param, value, label);
                    onViewChange === null || onViewChange === void 0 ? void 0 : onViewChange('events');
                } })), view === 'organizations' && (_jsx(UsageOrganizationsView, { params: params, organizationId: org, onSelectOrganization: setOrg, onOpenInterview: openInterview })), view === 'events' && (_jsx(UsageCallsView, { params: params, meta: meta, onOpenRecord: setSelectedRecord, onOpenInterview: openInterview })), _jsx(UsageRecordDrawer, { usageId: selectedRecord, onClose: () => setSelectedRecord(null) }), _jsx(InterviewCostDrawer, { interviewSlug: selectedInterview, onClose: () => setSelectedInterview(null) })] }));
}
